import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth, GRADES } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { Sparkles, Sun, Star, Cross, ArrowUpRight, Lock, Plus, X, Save } from "lucide-react";
import { toast } from "sonner";

const RITUAL_COVER = "https://static.prod-images.emergentagent.com/jobs/6221bdec-a1f4-4f2a-b3c7-5775bc29aacc/images/98edc85b162e33fdd54c42d9afd7e248509abebd6688a50b51fb8f2d78fd84a4.png";

const RITUAL_ICONS = {
  "lesser-pentagram": Star,
  "sun-greeting": Sun,
  "middle-pillar": Sparkles,
  "rosy-cross": Cross,
};

const EMPTY_ADD = {
  slug: "",
  min_grade: "Probationer",
  name_en: "",
  name_nl: "",
  tagline_en: "",
  tagline_nl: "",
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

export default function RitualsPage() {
  const { user } = useAuth();
  const { t, pick } = useI18n();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();

  const [rituals, setRituals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [add, setAdd] = useState(EMPTY_ADD);
  const [addBusy, setAddBusy] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rituals");
        setRituals(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load rituals");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onNameNl = (v) => {
    setAdd((d) => ({
      ...d,
      name_nl: v,
      slug: slugTouched ? d.slug : slugify(v),
    }));
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!add.slug.trim() || !add.name_en.trim() || !add.name_nl.trim()) return;
    setAddBusy(true);
    try {
      const { data } = await api.post("/rituals", { ...add, slug: add.slug.trim() });
      setRituals((cur) => [...cur, data]);
      toast.success(t("rituals.created_toast"));
      setAdd(EMPTY_ADD);
      setSlugTouched(false);
      setAddOpen(false);
      // Jump into the new ritual so admin can fill content immediately.
      navigate(`/rituals/${data.slug}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <main data-testid="rituals-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="label-small text-[#8B3A3A] mb-3">{t("rituals.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">{t("rituals.heading")}</h1>
          <p className="text-[#4A4238] mt-3 max-w-2xl">{t("rituals.intro")}</p>
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <button
              data-testid="rituals-open-add-btn"
              type="button"
              onClick={() => setAddOpen((v) => !v)}
              className="btn-primary inline-flex items-center gap-2 self-start select-none"
            >
              <Plus size={14} /> {t("rituals.add_btn")}
            </button>
          )}
          <div
            className="hidden md:block w-32 h-32 border border-[#C1B49A]"
            style={{ backgroundImage: `url(${RITUAL_COVER})`, backgroundSize: "cover", backgroundPosition: "center" }}
            aria-hidden
          />
        </div>
      </header>

      {addOpen && isAdmin && (
        <form
          onSubmit={submitAdd}
          className="border border-[#C1B49A] bg-[#E0D2B4]/40 p-6 md:p-8 mb-10 space-y-5"
          data-testid="ritual-add-form"
        >
          <p className="label-small text-[#8B3A3A] mb-2 flex items-center gap-2">
            <Plus size={12} /> {t("rituals.add_heading")}
          </p>
          <p className="text-xs italic text-[#7A7061]">{t("rituals.add_hint")}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <div>
              <label className="label-small block mb-2">{t("rituals.field_name_nl")}</label>
              <input
                data-testid="ritual-add-name-nl"
                required
                value={add.name_nl}
                onChange={(e) => onNameNl(e.target.value)}
                className="input-etched"
              />
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.field_name_en")}</label>
              <input
                data-testid="ritual-add-name-en"
                required
                value={add.name_en}
                onChange={(e) => setAdd((d) => ({ ...d, name_en: e.target.value }))}
                className="input-etched"
              />
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.field_tagline_nl")}</label>
              <input
                data-testid="ritual-add-tagline-nl"
                value={add.tagline_nl}
                onChange={(e) => setAdd((d) => ({ ...d, tagline_nl: e.target.value }))}
                className="input-etched"
              />
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.field_tagline_en")}</label>
              <input
                data-testid="ritual-add-tagline-en"
                value={add.tagline_en}
                onChange={(e) => setAdd((d) => ({ ...d, tagline_en: e.target.value }))}
                className="input-etched"
              />
            </div>
            <div>
              <label className="label-small block mb-2 flex items-center gap-2">
                {t("rituals.field_slug")}
                <span className="text-[10px] tracking-normal normal-case italic text-[#7A7061] font-normal">
                  {t("rituals.field_slug_hint")}
                </span>
              </label>
              <input
                data-testid="ritual-add-slug"
                required
                pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                value={add.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setAdd((d) => ({ ...d, slug: e.target.value.toLowerCase() }));
                }}
                className="input-etched font-mono text-xs"
                placeholder="rosy-cross"
              />
            </div>
            <div>
              <label className="label-small block mb-2">{t("rituals.field_min_grade")}</label>
              <select
                data-testid="ritual-add-min-grade"
                value={add.min_grade}
                onChange={(e) => setAdd((d) => ({ ...d, min_grade: e.target.value }))}
                className="bg-[#F4EEDF] border border-[#1A1918] px-3 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-none focus:outline-none focus:border-[#8B3A3A] w-full"
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => { setAddOpen(false); setAdd(EMPTY_ADD); setSlugTouched(false); }}
              className="btn-secondary inline-flex items-center gap-1.5 select-none"
            >
              <X size={12} /> {t("common.cancel")}
            </button>
            <button
              data-testid="ritual-add-submit-btn"
              type="submit"
              disabled={addBusy || !add.slug.trim() || !add.name_en.trim() || !add.name_nl.trim()}
              className="btn-primary inline-flex items-center gap-1.5 select-none"
            >
              <Save size={12} /> {addBusy ? t("rituals.creating") : t("rituals.create_submit")}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-[#7A7061] italic">{t("rituals.unsealing")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#C1B49A]">
          {rituals.map((r, i) => {
            const Icon = RITUAL_ICONS[r.slug] || Sparkles;
            const n = String(i + 1).padStart(2, "0");
            const cardBody = (
              <>
                <div className="flex items-start justify-between mb-6">
                  <Icon size={32} className={r.locked ? "text-[#7A7061]" : "text-[#8B3A3A]"} />
                  {r.locked ? (
                    <Lock size={20} className="text-[#7A7061]" />
                  ) : (
                    <ArrowUpRight size={20} className="text-[#7A7061] group-hover:text-[#8B3A3A] transition-colors" />
                  )}
                </div>
                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#7A7061] mb-3">
                  {t("rituals.ritual_num", { n })} · <span className={r.locked ? "text-[#7A7061]" : "text-[#8B3A3A]"}>{r.min_grade}</span>
                </p>
                <h2 className="font-display text-3xl text-[#1A1918] leading-tight mb-3">{pick(r, "name")}</h2>
                <p className="text-sm italic text-[#4A4238] leading-relaxed">{pick(r, "tagline")}</p>
              </>
            );
            const cellStyle = {
              borderRightWidth: i % 2 === 1 ? 0 : 1,
              borderBottomWidth: i < rituals.length - (rituals.length % 2 === 0 ? 2 : 1) ? 1 : 0,
              borderColor: "#C1B49A",
              borderStyle: "solid",
            };
            return r.locked ? (
              <div
                key={r.slug}
                data-testid={`ritual-locked-${r.slug}`}
                className="p-8 md:p-10 bg-[#EAE0CA]/30 opacity-60 cursor-not-allowed"
                style={cellStyle}
                title={t("rituals.locked_hint")}
              >
                {cardBody}
              </div>
            ) : (
              <Link
                key={r.slug}
                to={`/rituals/${r.slug}`}
                data-testid={`ritual-card-${r.slug}`}
                className="group p-8 md:p-10 bg-[#EAE0CA]/40 hover:bg-[#EAE0CA]/80 transition-colors block"
                style={cellStyle}
              >
                {cardBody}
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}
